/**
 * Single-row section picker for Charger Management.
 *
 * Replaces a wide horizontal tab strip when there are many sections — keeps
 * the header compact and opens a modal list to pick a section.
 */

import React, { useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { OcppTheme } from './OcppTheme';
import type { OcppTabDefinition } from './OcppTabBar';

export interface OcppTabPickerProps {
  tabs: OcppTabDefinition[];
  activeKey: string;
  onSelect: (key: string) => void;
}

const OcppTabPicker: React.FC<OcppTabPickerProps> = ({ tabs, activeKey, onSelect }) => {
  const [open, setOpen] = useState(false);
  const { height: windowHeight } = useWindowDimensions();
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
  const sheetMaxHeight = Math.min(420, Math.round(windowHeight * 0.62));
  const sheetHeaderReserve = 48;
  const listMaxHeight = Math.max(140, sheetMaxHeight - sheetHeaderReserve);

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        style={({ pressed }) => [styles.trigger, pressed && { opacity: 0.88 }]}
        accessibilityRole="button"
        accessibilityLabel={`Charger section: ${active?.label ?? 'Section'}. Opens a list to choose another section.`}
        accessibilityState={{ expanded: open }}
      >
        <View style={styles.triggerInner}>
          <Text style={styles.triggerLabel} numberOfLines={1}>
            {active?.label ?? 'Section'}
          </Text>
          <MaterialIcons name="arrow-drop-down" size={26} color={OcppTheme.textMuted} />
        </View>
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <View style={styles.modalRoot}>
          <Pressable
            style={styles.backdrop}
            onPress={() => setOpen(false)}
            accessibilityLabel="Dismiss section list"
            accessibilityRole="button"
          />
          <View style={styles.sheetAligner} pointerEvents="box-none">
            <View style={[styles.sheet, { maxHeight: sheetMaxHeight }]}>
              <Text style={styles.sheetTitle}>Charger section</Text>
              <ScrollView
                style={{ maxHeight: listMaxHeight }}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {tabs.map((tab) => {
                  const selected = tab.key === activeKey;
                  return (
                    <Pressable
                      key={tab.key}
                      disabled={tab.disabled}
                      onPress={() => {
                        onSelect(tab.key);
                        setOpen(false);
                      }}
                      accessibilityRole="button"
                      accessibilityState={{ selected, disabled: !!tab.disabled }}
                      style={({ pressed }) => [
                        styles.option,
                        selected && styles.optionSelected,
                        pressed && !tab.disabled && styles.optionPressed,
                        tab.disabled && styles.optionDisabled,
                      ]}
                    >
                      <Text
                        style={[styles.optionLabel, selected && styles.optionLabelSelected]}
                        numberOfLines={2}
                      >
                        {tab.label}
                      </Text>
                      {selected ? (
                        <MaterialIcons name="check" size={22} color={OcppTheme.accent} />
                      ) : (
                        <View style={styles.optionSpacer} />
                      )}
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default OcppTabPicker;

const styles = StyleSheet.create({
  trigger: {
    borderRadius: 10,
    backgroundColor: OcppTheme.surfaceMuted,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
  },
  triggerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    minHeight: 44,
  },
  triggerLabel: {
    flex: 1,
    color: OcppTheme.textPrimary,
    fontSize: 15,
    fontWeight: '600',
    marginRight: 4,
  },
  modalRoot: {
    flex: 1,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  sheetAligner: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    paddingHorizontal: 22,
  },
  sheet: {
    backgroundColor: OcppTheme.surface,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
    overflow: 'hidden',
  },
  sheetTitle: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 8,
    fontSize: 13,
    fontWeight: '700',
    color: OcppTheme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: OcppTheme.divider,
  },
  optionSelected: {
    backgroundColor: OcppTheme.accentDim,
  },
  optionPressed: {
    opacity: 0.92,
  },
  optionDisabled: {
    opacity: 0.45,
  },
  optionLabel: {
    flex: 1,
    color: OcppTheme.textSecondary,
    fontSize: 16,
    fontWeight: '500',
    marginRight: 12,
  },
  optionLabelSelected: {
    color: OcppTheme.accent,
    fontWeight: '700',
  },
  optionSpacer: {
    width: 22,
  },
});
